import * as React from "react";
import { X, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";

export type OptionType = {
  label: string;
  value: string;
  logoUrl?: string; // URL da imagem do logo
  description?: string; // Descrição opcional
};

interface MultiSelectProps {
  options: OptionType[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  className?: string;
  badgeClassName?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Selecionar itens...",
  emptyText = "Nenhum item encontrado.",
  className,
  badgeClassName,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = React.useCallback(
    (option: OptionType) => {
      const isSelected = selected.includes(option.value);
      
      if (isSelected) {
        onChange(selected.filter((value) => value !== option.value));
      } else {
        onChange([...selected, option.value]);
      }
    },
    [onChange, selected]
  );

  const handleRemove = React.useCallback(
    (value: string) => {
      onChange(selected.filter((item) => item !== value));
    },
    [onChange, selected]
  );

  const selectedOptions = React.useMemo(
    () => options.filter((option) => selected.includes(option.value)),
    [options, selected]
  );

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "min-h-10 h-auto w-full justify-between px-3 py-2",
            className
          )}
          // Atributos para evitar interação com preenchimento automático e extensões de senhas
          data-lpignore="true"
          // Removido autoComplete que estava causando erro de tipagem no Button
          type="button"
        >
          {selected.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className={cn(
                    "mr-1 mb-1 flex items-center gap-1 px-2 py-1",
                    badgeClassName
                  )}
                >
                  {option.logoUrl && (
                    <Avatar className="h-5 w-5 mr-1">
                      <AvatarImage src={option.logoUrl} alt={option.label} />
                      <AvatarFallback className="text-[10px]">
                        {option.label.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {option.label}
                  <span
                    role="button"
                    tabIndex={0}
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleRemove(option.value);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(option.value);
                    }}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </span>
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-full p-0" 
        align="start" 
        sideOffset={0}
        alignOffset={0}
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        {/* Removido o cabeçalho com "Produtos disponíveis" e botão de fechar */}
        <Command>
          {/* Removido o CommandInput (campo de busca) conforme solicitado */}
          <CommandEmpty>{emptyText}</CommandEmpty>
          <CommandGroup 
            className="max-h-48 overflow-auto scrollable-content"
            onWheel={handleWheel}
          >
            {options.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <CommandItem
                  key={option.value}
                  onSelect={() => handleSelect(option)}
                  className="flex items-center gap-2 h-10 py-0"
                >
                  <div
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible"
                    )}
                  >
                    <Check className="h-3 w-3" />
                  </div>
                  
                  {/* Logo do produto */}
                  <Avatar className="h-6 w-6 min-w-6 mr-2">
                    <AvatarImage 
                      src={option.logoUrl} 
                      alt={option.label} 
                      className="object-cover"
                    />
                    <AvatarFallback className="text-xs bg-primary/10">
                      {option.label.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Nome do produto sem descrição */}
                  <span className="font-medium truncate">{option.label}</span>
                  
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
      {/* Estilo para remover ícones de extensões de senhas e preenchimento automático */}
      <style>
        {`
          /* Evitar ícones de preenchimento automático */
          .no-autocomplete::-webkit-credentials-auto-fill-button,
          input[data-lpignore="true"]::-webkit-credentials-auto-fill-button {
            display: none !important;
            visibility: hidden;
            pointer-events: none;
          }

          /* Para o Chrome */
          input[data-lpignore="true"]::-webkit-textfield-decoration-container {
            visibility: hidden;
          }

          /* Para Firefox, Edge, etc */
          input[data-lpignore="true"] {
            background-image: none !important;
          }
          
          /* Melhoria de scroll */
          .scrollable-content {
            overflow-y: auto !important;
            scrollbar-width: thin;
            scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
          }
          
          .scrollable-content::-webkit-scrollbar {
            width: 6px;
          }
          
          .scrollable-content::-webkit-scrollbar-thumb {
            background-color: rgba(155, 155, 155, 0.5);
            border-radius: 3px;
          }
          
          .scrollable-content::-webkit-scrollbar-track {
            background: transparent;
          }
        `}
      </style>
    </Popover>
  );
}
